import { Injectable } from '@angular/core';
import { BehaviorSubject, mergeMap, Observable, of, Subject, Subscription } from 'rxjs';
import { OfflineSyncType } from 'src/app/shared/components/grid/cells/grid-cell-extension/grid-cell-extension.component';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { SyncItem, SyncItemType } from 'src/models/ts/sync-item';

export enum SyncState {
  IDLE,
  SYNCING,
  PAUSED,
  FAILED
}
@Injectable({
  providedIn: 'root'
})
export class SyncQueueService {
  // Play/pause state
  public syncState$ = new BehaviorSubject<SyncState>(SyncState.IDLE);

  public showProgressBar = false;
  // Queue holds factory functions that return observables
  private queue: Map<string, SyncItem> = new Map();
  public requestingItem: SyncItem | undefined;
  private queueSubscription: Subscription | undefined;
  // Emits when each item completes
  private currentItemCompleteSubject = new Subject<any>();
  public currentItemComplete$ = this.currentItemCompleteSubject.asObservable();
  private currentItemCompleteSubject2 = new Subject<any>();
  public currentItemComplete2$ = this.currentItemCompleteSubject2.asObservable();
  private isRunning = false;
  private progressBarTimeout: NodeJS.Timeout;

  //TODO RV: Refactor to track the progress per item
  private _currentProgress: number = 0;
  private _totalProgress: number = 0;

  public get totalProgress(): number {
    return this._totalProgress;
  }

  public get currentProgress(): number {
    return this._currentProgress;
  }

  public get nrOfItemsInQueue(): number {
    return this.queue.size;
  }

  public isInQueue(key: string): boolean {
    return this.queue.has(key);
  }

  public get queueItems(): SyncItem[] {
    const queueItems = Array.from(this.queue.values());
    if (this.requestingItem && this.syncState$.getValue() !== SyncState.PAUSED) {
      return [this.requestingItem, ...queueItems];
    }
    return queueItems;
  }

  public addToQueue(item: SyncItem): void {
    this.increaseTotalProgress(item.observables.length);
    if(item.type === SyncItemType.SINGLE) {
      if(this.isInQueue(item.id)) {
        const existingItem = this.queue.get(item.id) as SyncItem;
        existingItem.observables.push(...item.observables);
        existingItem.totalProgress += item.observables.length;
        this.queue.set(item.id, existingItem)
      }else if(this.requestingItem && this.requestingItem.id === item.id) {
        this.requestingItem.observables.push(...item.observables);
        this.requestingItem.totalProgress += item.observables.length;
      }else {
        // If the item is not in the queue and not the requesting item, add it to the queue
        this.queue.set(item.id, item);
      }
    }else {
      if (this.isInQueue(item.id)) {
        // Check if it is not type single 
        console.warn(`An item with id ${item.id} already exists in the queue. Skipping addition.`);
        return;
      }
      
      this.queue.set(item.id, item);
    }
    if (!this.isRunning && this.syncState$.getValue() !== SyncState.PAUSED) {
      this.processQueue();
    }
  }

  public play(): void {
    this.syncState$.next(SyncState.SYNCING);
    if (!this.isRunning) {
      this.processQueue();
    }
  }

  public retry(item: SyncItem): void {
    this.syncState$.next(SyncState.SYNCING);
    item.observables = item.failedObservables ?? [];
    item.failedObservables = [];
    item.totalProgress =item.observables.length;
    item.currentProgress = 0;

    if (!this.isRunning) {
      this.processQueue(item);
    }
  }

  public pause(): void {
    this.syncState$.next(SyncState.PAUSED);
    this.isRunning = false;
    this.queueSubscription?.unsubscribe();
    // add requesting item back to the queue
    if (this.requestingItem) {
      this.queue.set(this.requestingItem.id, this.requestingItem);
    }
  }

  public setInstanceState(collectionId: number, instanceId: number, state: OfflineSyncType): void {
    this.currentItemCompleteSubject2.next({ collectionId, instanceId, state: state });
  }

  public resetProgress(): void {
    this._currentProgress = 0;
    this._totalProgress = 0;
    this.showProgressBar = false;
  }

  private increaseTotalProgress(number: number): void {
    if (number != undefined && number > 0) {
      this._totalProgress += number;
      this.showProgressBar = true;
      if (this.progressBarTimeout) clearTimeout(this.progressBarTimeout);
    }
  }

  public increaseCurrentProgress(number: number = 1): void {
    this._currentProgress += number;
    this.showProgressBar = true;
    if (this.progressBarTimeout) clearTimeout(this.progressBarTimeout);
    if (this._currentProgress === this.totalProgress) {
      // All API calls are completed, hide progress bar
      //this.progressBarTimeout = setTimeout(() => {
        this.resetProgress();
     // }, 5000);
    }
  }

  private processQueue(requestItem?: SyncItem): void {
    if (this.queue.size === 0 || this.syncState$.getValue() === SyncState.PAUSED || !this.queueItems.find(item => item.observables.length)) {
      if(this.queueItems.find(item => item.failedObservables?.length)) this.syncState$.next(SyncState.FAILED);
      this.isRunning = false;
      return;
    }

    let id: string;
    if(requestItem){
      id = requestItem.id;
    }else{
      id = this.queue.keys().next().value;
    }

    this.requestingItem = this.queue.get(id);
    this.queue.delete(id);

    if (!this.requestingItem) {
      this.isRunning = false;
      return;
    }

    this.isRunning = true;
    this.syncState$.next(SyncState.SYNCING);

    const items: Observable<any> = of(...this.requestingItem.observables).pipe(mergeMap((request) => request, 1));

    this.queueSubscription = items.subscribe({
      next: (value) => {
        if (value.originalError instanceof HttpErrorResponse) {
          this.currentItemCompleteSubject.next({ id: id, type: this.requestingItem?.type, hasFailed: true, data: value.data});
          this.setInstanceState(value.data.collectionId, value.data.instanceId, OfflineSyncType.Failed);
          this.setCurrentItemAsFailed();
          return;
        }

        this.increaseCurrentProgress();
        if(this.requestingItem) this.requestingItem.currentProgress ++;
        this.currentItemCompleteSubject.next({ id: id, type: this.requestingItem?.type, result: value});
        if(value.formData) this.setInstanceState(value.formData.CollectionsID, value.formData.InstancesID, OfflineSyncType.Downloaded);
        this.requestingItem?.observables.shift();
      },
      error: (error) => {
        console.error(`Error processing item ${id}:`, error);
        this.setCurrentItemAsFailed();
        this.currentItemCompleteSubject.next({ id: id, completed: this.requestingItem?.observables.length === 0, type: this.requestingItem?.type, hasFailed: true, data: error.data});
        this.proceedNextInQueue();
      },
      complete: () => {
        this.currentItemCompleteSubject.next({ id: id, completed: true, type: this.requestingItem?.type, hasFailed: this.requestingItem?.failedObservables && this.requestingItem.failedObservables.length > 0  });
        this.proceedNextInQueue();
      }
    });
  }

  private proceedNextInQueue(): void {
    this.isRunning = false;
    if(this.requestingItem?.observables.length === 0 && this.requestingItem.failedObservables && this.requestingItem.failedObservables.length > 0) {
      this.queue.set(this.requestingItem.id, this.requestingItem);
      this.syncState$.next(SyncState.FAILED);
    } else if(this.requestingItem?.observables && this.requestingItem.observables.length  > 0) {
      this.queue.set(this.requestingItem.id, this.requestingItem);
    }
    this.requestingItem = undefined;
    this.processQueue();
  }

  private setCurrentItemAsFailed(): void {
    this.increaseCurrentProgress();
    const request = this.requestingItem?.observables.shift();
    if (request && this.requestingItem) {
      this.requestingItem.currentProgress++;
      if (!this.requestingItem.failedObservables)
        this.requestingItem.failedObservables = [];
      this.requestingItem.failedObservables.push(request);
    }
  }
}
